import Modal from '../../components/Modal';
import { Events } from '../../utils/dom/html-events';
import Manipulator from '../../utils/dom/manipulator';
import Request, { ApiMode } from '../../utils/request';
import EventHandler from '../../utils/dom/event-handler';
import { startLoadingAnimation, stopLoadingAnimation } from '../../utils/dom/loading';
import { ClassNames } from '../../utils/dom/css-classes';
import SelectorEngine from '../../utils/dom/selector-engine';
import ModalPageLayout from '../../components/ModalPageLayout';
import FormGenerator, { FormTypes } from '../../components/FormGenerator';
import { householdQuestionRenderers } from '../HouseholdAccounts/formQuestions/questionRenderers';


const SELECTOR_HOLDING_PAGE = '.p-youth-starter-offer__holding-page';
const SELECTOR_NOTIFICATION_BTN = '.p-youth-starter-offer__holding-page-notification-btn';
const SELECTOR_NOTIFICATION_CONTAINER = '.p-youth-starter-offer__holding-page-notification';
const SELECTOR_NOTIFICATION_SUCCESS = '.p-youth-starter-offer__holding-page-notification-success';
const SELECTOR_NOTIFICATION_ERROR_CONTAINER = '.p-youth-starter-offer__holding-page-error';
const SELECTOR_NOTIFICATION_ERROR_BTN = '.p-youth-starter-offer__holding-page-error-btn';

const SELECTOR_YOUTH_STARTER_OFFER_LANDING_PAGE = '.p-youth-starter-offer-landing-page';
const SELECTOR_YOUTH_STARTER_OFFER_LANDING_PAGE_EXIT_BUTTON = '.p-youth-starter-offer-exit-button';
const SELECTOR_GET_STARTED = '.js-youth-get-started';

const SELECTOR_YOUTH_ORDER_WRAPPER = '.p-youth-starter-offer';
const SELECTOR_YOUTH_ORDER_MODAL = '#js-youth-starter-offer-modal';
const SELECTOR_YOUTH_ORDER_STEPS_WRAPPER = '.p-youth-starter-offer-steps-wrapper';
const SELECTOR_YOUTH_ORDER_QUESTIONS = '.p-youth-starter-offer-questions';

const SELECTOR_SUCCESS_MODAL = '.p-youth-starter-offer-success';

const KEY_OBJECT = 'fieldName';
const VALUE_OBJECT = 'value';
const IS_LOCAL = false;

const SINGLE_CHILD_COST = 4.99;

class YouthStarterOffer {

    get stepsData() {
		return this._stepsData;
	}

	set stepsData(_stepsData) {
		this._stepsData = _stepsData;
		if (_stepsData?.length > 0) {
			this.initiateFormGenerator(_stepsData);
		}
	}

	constructor() {
        this.element = SelectorEngine.findOne(SELECTOR_YOUTH_ORDER_WRAPPER);
        if(!this.element) {
            return;
        }
        this.init();
	}

	init() {
		/*
			No Stock available - As per ticket #30303 we need to show a notification page if no stock is available, 
			BE will toggle u-hidden class on pageload,
		*/
		this.holdingPage = SelectorEngine.findOne(SELECTOR_HOLDING_PAGE, this.element);
		this.submitNotificationEndpoint = Manipulator.getDataAttribute(this.element, 'notification-endpoint');
		this.submitNotificationBtn = SelectorEngine.findOne(SELECTOR_NOTIFICATION_BTN, this.element);
		this.notificationContainer = SelectorEngine.findOne(SELECTOR_NOTIFICATION_CONTAINER, this.element);
		this.notificationSuccess = SelectorEngine.findOne(SELECTOR_NOTIFICATION_SUCCESS, this.element);

		this.submitNotificationErrorContainer = SelectorEngine.findOne(SELECTOR_NOTIFICATION_ERROR_CONTAINER, this.element);
		this.submitNotificationErrorContainerBtn = SelectorEngine.findOne(SELECTOR_NOTIFICATION_ERROR_BTN, this.element);

		if(this.submitNotificationBtn) {
			EventHandler.on(this.submitNotificationBtn, Events.Click, () => {
				this.submitNotification();
			});
		}

		if(this.submitNotificationErrorContainerBtn && this.submitNotificationErrorContainer) {
			EventHandler.on(this.submitNotificationErrorContainerBtn, Events.Click, () => {
				this.submitNotificationErrorContainer.classList.add(ClassNames.Hidden);
			});
		}

		// Stock available - Show onboarding screen and allow journey start
		this.modalElement = SelectorEngine.findOne(SELECTOR_YOUTH_ORDER_MODAL, this.element);
		this.exitBtnRedirectionLink = Manipulator.getDataAttribute(this.element, 'questions-back-button-redirection-link');
		this.youthStarterOfferQuestion = SelectorEngine.findOne(SELECTOR_YOUTH_ORDER_QUESTIONS, this.element);
		this.stepContainer = SelectorEngine.findOne(SELECTOR_YOUTH_ORDER_STEPS_WRAPPER, this.element);

		this.stepsEndpoint = Manipulator.getDataAttribute(this.element, 'steps-endpoint');
		this.submitEndpoint = Manipulator.getDataAttribute(this.element, 'submit-endpoint');

		// As per ticket #30303 BE will toggle u-hidden class on pageload based on stock availability, so checking if modal has u-hidden class or not to decide whether to show onboarding or not
		if(!this.modalElement?.classList.contains(ClassNames.Hidden)) {
			this.modal = new Modal(this.modalElement, { backdrop: 'static' });
			this.costPerChild = Manipulator.getDataAttribute(this.element, 'cost-per-child') ?? SINGLE_CHILD_COST;
			this.productId = Manipulator.getDataAttribute(this.element, 'salesforce-product-id');
			this.modal.show(); 

			this.landingPage = SelectorEngine.findOne(SELECTOR_YOUTH_STARTER_OFFER_LANDING_PAGE, this.element);
			this.exitButton = SelectorEngine.findOne(SELECTOR_YOUTH_STARTER_OFFER_LANDING_PAGE_EXIT_BUTTON, this.element);
			this.getStartedBtn = SelectorEngine.findOne(SELECTOR_GET_STARTED, this.element);

			if (this.getStartedBtn) {
				EventHandler.on(this.getStartedBtn, Events.Click, () => {
					this.startJourney();
				});
			}

			this.successModal = SelectorEngine.findOne(SELECTOR_SUCCESS_MODAL, this.element);
			this.orderSuccessModalInstance = new ModalPageLayout(this.successModal);
		}
	}


	startJourney() {
		if (this.landingPage) {
			this.landingPage.classList.add(ClassNames.Hidden);
		}

		if (this.exitButton) {
			this.exitButton.classList.add(ClassNames.Hidden);
		}

		if (this.stepContainer) {
			this.stepContainer.classList.remove(ClassNames.Hidden);
		}

		this.fetchYouthStarterOfferSteps();
	}

    async fetchYouthStarterOfferSteps() {
		try {
			const url = this.stepsEndpoint;
			const response = await Request.get(
				url,
				{},
				{
					local: IS_LOCAL,
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);
			const { steps, extraParams, error } = response;

			if(extraParams) {
				this.extraParams = extraParams;
			}

			if (steps?.length) {
				this.stepsData = steps;
			}

			if (!!error?.hasError) {
				throw error;
			}
		} catch (error) {
			if (error.hasError) {
				console.log('Error :', error);
			}
		}
	}

	youthStarterOfferSubmitHandler(payload) {
		return Request.post(this.submitEndpoint, payload,
			{
				local: IS_LOCAL,
				req: {
					apiMode: ApiMode.LtaError
				}
			}
		);
	}

    initiateFormGenerator(stepsData) {
		this.formGenerator = new FormGenerator(this.youthStarterOfferQuestion, {
			questions: stepsData,
			totalSteps: 3,
			hasSeparator: true,
			formType: FormTypes.StepForm,
			themeColor: 'color-lta-accent-orange',
            stepVisibility: [true, true, true],
			isBackButtonEnabled: true,
			submitButton: { title: 'Pay now' },
			wrapperClasses: ['p-youth-start-offer__fixed-width', 'p-youth-start-offer__fixed-width', 'p-youth-start-offer__full-width'],
			checkoutSuccess: this.checkoutSuccess.bind(this),
			saveAddress: false,
			userDetails: {
				name: this.extraParams?.name,
				email: this.extraParams?.email
			},
			questionRenderers: householdQuestionRenderers,
			backButtonHandlerFirstStep: () => {
				this.landingPage.classList.remove(ClassNames.Hidden);
				this.stepContainer.classList.add(ClassNames.Hidden);

				if (this.exitButton) {
					this.exitButton.classList.remove(ClassNames.Hidden);
				}
			},
			isExitButtonEnabled: true,
			exitButtonLabel: 'Close',
			exitButtonHandler: () => {
				window.location.href = this.exitBtnRedirectionLink;
				// destroy the form generator
				this.formGenerator.unload();
			}
		});

		this.setTotalCost(0, 0);
		
		this.formGenerator.onStepChange((currentStep) => {
			const nextButtonWrapper = SelectorEngine.findOne('.lta-form-generator__form-next', this.element);
			const payload = this.prepareRequestObject(this.formGenerator.answers);
			const totalChildren = payload?.children?.length;
			const totalCost = this.setTotalCost(this.costPerChild, totalChildren);
			const checkoutQuestionRef = this.formGenerator.questions.find(question => question.questionType === 'checkout')?.ref;
			if (!checkoutQuestionRef) {
				return;
			}
			checkoutQuestionRef.data = {
				...checkoutQuestionRef.data,
				count: totalChildren,
				cost: this.costPerChild,
				total: totalCost,
				productId: this.productId
			}
			if (currentStep === 3) {
				if (nextButtonWrapper) {
					nextButtonWrapper.classList.add(ClassNames.Hidden);
				}
				checkoutQuestionRef.initPayment();
			} else {
				if (nextButtonWrapper) {
					nextButtonWrapper.classList.remove(ClassNames.Hidden);
				}
			}

		});
		
        this.formGenerator.onSubmit(async (answer) => {
			this.checkoutSuccess();
        });
    }

    prepareRequestObject(stepsAnswers) {
		let answerList = {};
		for (const stepKey in stepsAnswers) {
			const stepAnswersObjects = stepsAnswers[stepKey];
			for (const questionKey in stepAnswersObjects) {
				const answerObject = stepAnswersObjects[questionKey];
				const formatObject = {};
				formatObject[answerObject[KEY_OBJECT]] = answerObject[VALUE_OBJECT];
				answerList = {
					...answerList,
					...formatObject
				};
			}
		}
		if(answerList.address) {
			answerList.address = {
				"mailingStreet" : answerList.address?.street,
				"postCode" : answerList.address?.postCode,
				"mailingCity" : answerList.address?.city,
				"county" : answerList.address?.state,
				"addressCountry" : "United Kingdom"
			}
		}

		// NEW: Map children details dynamically
		let children = [];
		if (Array.isArray(answerList.totalChildren)) {
			children = answerList.totalChildren
				.filter(child => child.isChecked)
				.map(child => ({
					"childFirstName" : child.firstName,
					"childLastName" : child.lastName,
					"childDateOfBirth" : child.dateOfBirth,
					"childGender" : child.gender,
					"sfid" : child?.sfid,
					...child

				}));
		}
		delete answerList.totalChildren;
		answerList.source = "Youth Starter";
		answerList.children = children;
		if(this.extraParams) {
			answerList = {
				...answerList,
				...this.extraParams
			}
		}
		
		return answerList;
	}

	async submitNotification() {
		try {
			const responseData = await Request.post(this.submitNotificationEndpoint);
			const { success } = responseData;
			if (success) {
				this.notificationSuccess.classList.remove(ClassNames.Hidden);
				this.notificationContainer.classList.add(ClassNames.Hidden);
			} else {
				this.submitNotificationErrorContainer.classList.remove(ClassNames.Hidden);
			}
		} catch {
			this.submitNotificationErrorContainer.classList.remove(ClassNames.Hidden);
		}
	}

	async checkoutSuccess(afterAuthentication, coupons, discountType) {
		try {
			startLoadingAnimation();
			const payload = this.prepareRequestObject(this.formGenerator.answers);
			payload.promoCodes = coupons,
			payload.promoCodeType = discountType
;
			const data = await this.youthStarterOfferSubmitHandler({ 
				...payload, 
				IS_REQ_AUTHORIZATION_SUCCESS: afterAuthentication 
			});				
			
			if((data.Success || data.success) && data.Status !== "REQ_AUTHORIZATION") {
				this.modal.hide();
				this.orderSuccessModalInstance.showModal();
			} else {
				console.log("error");
			}
			return data;
		} catch (error) {
			console.log('Error :', error);
			return {
				Success: false,
				success: false,
				ErrorMessage: error.message || "Something went wrong"
			};
		}finally {
            stopLoadingAnimation();
        }
	}

	setTotalCost(cost, totalChildren) {
		window.lta = window.lta || {}
		window.lta.lta_payment = window.lta?.lta_payment || {}
		window.lta.lta_payment.basket = window.lta.lta_payment?.basket || {}
		window.lta.lta_payment.basket.totalCost = cost * totalChildren;
		return cost * totalChildren;
	}
}

const globalYouthStarterOffer = new YouthStarterOffer();

export default globalYouthStarterOffer;

